import { Chip, Stack, Typography } from "@mui/material";
import {
  TripSortCriteria,
  GetDriverListQuery,
  GetDriverListQueryVariables,
  AssetStatus,
  CustomFieldContext,
  TripAssetTypes,
} from "../../../graphql/generated";
import { TableField } from "../../common/LynksTable/LynksTable";
import SmartLynksTable, {
  QueryFn,
} from "../../common/SmartLynksTable/SmartLynksTable";
import formatPhoneNumber from "../../../utils/labels/formatPhoneNumber";
import DriverLicenseExpiryWarning from "../DriverLicenseExpiryWarning/DriverLicenseExpiryWarning";
import DriverMedicalExpiryWarning from "../DriverMedicalExpiryWarning/DriverMedicalExpiryWarning";
import { useTranslation } from "react-i18next";
import { capitalize } from "lodash";
import MaintenanceWarningsContainer from "../../maintenance/MaintenanceWarnings";
import QualificationWarningsContainer from "../../qualifications/QualificationWarnings";
import { useState } from "react";

type DriverListItem = GetDriverListQuery["drivers"]["data"][0];

export default function DriversList({
  query,
  renderHeaderActions,
}: {
  query: QueryFn<DriverListItem, "drivers", TripSortCriteria>;
  renderHeaderActions?: () => React.ReactNode;
}) {
  const { t } = useTranslation(["users", "assets", "common"]);

  const [additionalQueryVariables, setAdditionalQueryVariables] =
    useState<GetDriverListQueryVariables>({ includeInactive: true });

  const fields: TableField<DriverListItem, AssetStatus>[] = [
    {
      value: (driver) => driver.license?.licenseNumber,
      label: capitalize(t("driver.license.one", "License")),
      type: "string",
      sortBy: "license.licenseNumber",
    },
    {
      value: (driver) => {
        return (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <DriverLicenseExpiryWarning driver={driver} />
            <DriverMedicalExpiryWarning driver={driver} />
            <Typography>
              {driver.firstname} {driver.lastname}
            </Typography>
          </Stack>
        );
      },
      label: t("driver.name", "Name"),
      type: "custom",
      sortBy: "firstname",
      rawValue(item) {
        return `${item.firstname} ${item.lastname}`;
      },
    },
    {
      value: (driver) => formatPhoneNumber(driver.phoneNumber),
      label: t("driver.contact", "Contact"),
      type: "string",
      sortBy: "phoneNumber",
    },
    {
      value: (driver) => driver.domicileEntity?.name,
      label: t("driver.domicile", "Domicile"),
      type: "string",
      sortBy: "domicile.name",
    },
    {
      value: "eldId",
      label: t("driver.eldId", "ELD ID"),
      type: "string",
      sortBy: "eldId",
    },
    {
      value: (driver) => (
        <Stack direction="row" spacing={1}>
          {driver.tags?.map((tag) => (
            <Chip label={tag} />
          ))}
        </Stack>
      ),
      sortBy: "tags",
      label: t("common:tags"),
      type: "string",
    },
    {
      value: (driver) => (
        <MaintenanceWarningsContainer
          assetType={TripAssetTypes.Driver}
          assetId={driver._id}
        />
      ),
      label: capitalize(t("assets:maintenance")),
      type: "string",
    },
    {
      value: (driver) => (
        <QualificationWarningsContainer
          assetType={TripAssetTypes.Driver}
          assetId={driver._id}
        />
      ),
      label: capitalize(t("assets:qualifications")),
      type: "string",
    },
    {
      value: (driver) => driver.status,
      label: t("assets:status", "Status"),
      valueLabel: (status) => t(`common:statusTypes.${status}`),
      type: "enum",
      colors: {
        ACTIVE: "primary",
        INACTIVE: "default",
      },
      values: Object.values(AssetStatus),
      sortBy: "status",
    },
  ];

  return (
    <SmartLynksTable
      query={query}
      dataKey="drivers"
      fields={fields}
      additionalQueryVariables={additionalQueryVariables}
      detailsUrlPrefix="/drivers/details"
      id="drivers-list"
      customFieldContext={[CustomFieldContext.Driver]}
      showGroups
      renderHeaderActions={renderHeaderActions}
      onFilterModelChange={(model, details) => {
        const domicileField = model.items.find(
          (item) => item.field === "domicileEntity.name"
        );
        if (domicileField?.value) {
          setAdditionalQueryVariables({
            where: { domicile: domicileField.value },
            includeInactive: true,
          });
        } else {
            setAdditionalQueryVariables({ includeInactive: true });
        }
      }}
      selectable
    />
  );
}
